import { Fade, Stack, styled } from '@mui/material'
import { TooltipText } from 'components/TooltipText'
import { memo, useCallback, useRef, useState } from 'react'
import { useAppDispatch } from 'store'
import { TradeType } from 'store/market/types'
import { updatePerpPostOnlyAction } from 'store/perp/actions'
import { updatePlaceOrderTIFAction } from 'store/settings/actions'
import { useCurrentPlaceOrderTIF } from 'store/settings/hooks'
import { TifType } from 'store/settings/types'
import { useOnClickOutside } from 'usehooks-ts'
import { themeValue } from 'styles'
import { ReactComponent as ArrrowSvg } from './arrow.svg'
import clsx from 'clsx'
import { ReactComponent as ArrowSelectedSvg } from './selected.svg'

const StyledPlaceOrderTIF = styled(Stack)`
  flex-direction: row;
  font-size: 14px;
  line-height: 1.4;
  position: relative;
`
const CurrentTIF = styled(Stack)`
  flex-direction: row;
  justify-content: center;
  min-width: 42px;
  padding-left: 4px;
  border-radius: 2px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease-in-out;
  color: ${({ theme }) => theme.color.text100};
  font-weight: 600;
  align-items: center;
  /* &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    background-color: transparent;
    width: 100%;
    height: 5px;
    right: 0;
  } */

  &:active {
    background-color: ${({ theme }) => theme.color.bgGray};
  }

  svg.arrow {
    color: ${({ theme }) => theme.color.text50};
    margin-left: 3px;
    transform: rotate(180deg);
    transition: all 0.4s ease;
  }

  &.active {
    svg.arrow {
      color: ${({ theme }) => theme.color.primary30};
      transform: rotate(0deg);
    }
  }
`
const Popup = styled(Stack)`
  position: absolute;
  top: calc(100% + 10px);
  right: 0px;
  width: 200px;
  /* height: 100px; */
  padding: 8px;
  box-sizing: border-box;
  z-index: 2;
  background-color: ${() =>
    themeValue('rgba(30, 35, 41, 1)', 'rgba(249, 249, 250, 1)')} !important;
  border-radius: 4px;
  border: 1px solid;
  border-color: ${() =>
    themeValue('rgba(42, 49, 57, 1)', 'rgba(240, 242, 243, 1)')} !important;
`
const PopupOption = styled('div')`
  transition: background-color 0.2s ease-in-out;
  cursor: pointer;
  font-weight: 400;
  line-height: 1.4;
  /* text-align: left; */
  margin-bottom: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  font-size: 14px;
  color: rgba(229, 234, 238, 1);
  svg {
    display: none;
  }
  &:hover {
    background-color: rgba(42, 49, 57, 1);
    border-radius: 4px;
  }
  &.active {
    font-weight: 600;
    color: ${({ theme }) => theme.palette.primary.main};
    cursor: default;
    svg {
      display: block;
    }
  }
  &:last-child {
    margin-bottom: 0px;
  }
`

const TifTypeList = Object.values(TifType)
export const PlaceOrderTIF = memo<{ tradeType: TradeType }>(({ tradeType }) => {
  const dispatch = useAppDispatch()
  const currentTIF = useCurrentPlaceOrderTIF()[tradeType]
  const [popupOpen, setPopupOpen] = useState(false)
  const ref = useRef(null)

  useOnClickOutside(ref, () => {
    setPopupOpen(false)
  })

  const handlePopupOpen = useCallback((open?: boolean) => {
    if (open === undefined) {
      setPopupOpen((prev) => !prev)
    } else {
      setPopupOpen(open)
    }
  }, [])

  const handleOptionClick = useCallback(
    (tif: TifType) => {
      handlePopupOpen(false)
      dispatch(updatePlaceOrderTIFAction({ tif, tradeType }))

      // If the TIF is not GTC, then disable post-only
      if (tif !== TifType.GTC) {
        dispatch(updatePerpPostOnlyAction(false))
      }
    },
    [dispatch, handlePopupOpen, tradeType],
  )
  return (
    <StyledPlaceOrderTIF ref={ref}>
      <TooltipText
        sx={(theme) => ({ color: theme.color.text50 })}
        title={
          <Stack spacing={1}>
            <div>
              <b>GTC (Good Till Cancel):</b> Orders remain in effect until
              they're either executed or manually canceled.
            </div>
            <div>
              <b>IOC (Immediate or Cancel):</b> Any part of the order that isn’t
              immediately filled will be canceled immediately.
            </div>
            <div>
              <b>FOK (Fill or Kill):</b> Orders are either filled immediately or
              killed.
            </div>
          </Stack>
        }
        placement="top"
      >
        TIF
      </TooltipText>
      <CurrentTIF
        className={clsx({ active: popupOpen })}
        onClick={() => handlePopupOpen(!popupOpen)}
      >
        {currentTIF}
        <ArrrowSvg className="arrow" />
        <Fade in={popupOpen}>
          <Popup>
            {TifTypeList.map((tif) => (
              <PopupOption
                key={tif}
                className={clsx({ active: tif === currentTIF })}
                onClick={(event) => {
                  event.stopPropagation()
                  handleOptionClick(tif as TifType)
                }}
              >
                <span>{tif}</span>
                <ArrowSelectedSvg />
              </PopupOption>
            ))}
          </Popup>
        </Fade>
      </CurrentTIF>
    </StyledPlaceOrderTIF>
  )
})
